import { useEffect, useState } from 'react';

import { bind } from 'bind-event-listener';

import { getDocument } from '@atlaskit/browser-apis';

import { normalizeHash } from '../../../common/deep-link';

const readToken = (): string => normalizeHash(getDocument()?.location.hash ?? '');

/**
 * The normalized token of the section the URL fragment currently names, or ''
 * when there is none. Reads the live hash rather than tracking clicks, so a
 * deep link, a manually edited fragment and a back/forward step all report the
 * section as reliably as a click on one of the nav bar's own anchor items.
 *
 * `hashchange` is enough to stay current: `setHash` and `clearHash` both
 * dispatch it (the native `replaceState` they use fires none), and `setHash`
 * does so even when the fragment is unchanged, so nothing that moves the
 * fragment in this app goes unobserved.
 *
 * The token is deliberately not validated against `HASH_TO_ANCHOR_ID` here.
 * Callers match it against their own item list, which is narrower than that
 * map (the nav bar omits `about`), and an unknown token simply matches
 * nothing.
 */
export const useActiveAnchorToken = (): string => {
	const [token, setToken] = useState(readToken);

	useEffect(() => {
		// The first render may precede the fragment being in place.
		setToken(readToken());
		return bind(window, {
			type: 'hashchange',
			listener: () => setToken(readToken()),
		});
	}, []);

	return token;
};
